<template>
    <div class="bills-payment-container">
        <!-- Main development banner -->
        <v-alert type="warning" class="development-banner" dense icon="construction">
            <strong>🚧 UNDER CONSTRUCTION 🚧</strong> - This page is currently in development. Features are limited and may not work properly.
        </v-alert>

        <!-- Header with construction badge -->
        <v-toolbar dense color="primary darken-2" dark flat class="header-animated">
            <v-btn icon @click="goBack" class="back-btn">
                <v-icon>arrow_back</v-icon>
            </v-btn>
            <v-toolbar-title class="toolbar-title">
                <v-icon left>receipt</v-icon>
                Bills & Services Payment
                <v-chip small color="warning" text-color="white" class="ml-2 construction-chip">
                    <v-icon small left>construction</v-icon>
                    BETA
                </v-chip>
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn icon @click="refreshData" class="refresh-btn">
                <v-icon>refresh</v-icon>
            </v-btn>
        </v-toolbar>

        <v-container class="mt-2">
            <v-layout row wrap>
                <v-flex xs12 md8 offset-md2 lg6 offset-lg3>
                    <!-- Construction overlay -->
                    <div class="construction-overlay" v-if="showConstructionOverlay">
                        <div class="construction-content">
                            <v-icon color="warning" size="64">construction</v-icon>
                            <h3 class="construction-title">Feature Under Development</h3>
                            <p class="construction-message">
                                This section is currently under construction.
                                <br>It will be available soon.
                            </p>
                            <v-btn color="warning" @click="showConstructionOverlay = false" depressed>
                                Got It
                            </v-btn>
                        </div>
                    </div>

                    <!-- Wallet Balance Card with construction indication -->
                    <v-card class="mb-6 wallet-card" color="primary" dark>
                        <div class="construction-badge">BETA</div>
                        <v-card-text class="wallet-content">
                            <div class="text-xs-center">
                                <div class="wallet-icon">
                                    <v-icon large>account_balance_wallet</v-icon>
                                </div>
                                <div class="caption wallet-label">Available Balance</div>
                                <div class="display-1 font-weight-bold wallet-amount">
                                    {{ formatCurrency(walletBalance) }}
                                </div>
                                <div class="wallet-actions">
                                    <v-btn small outline class="topup-btn" @click="showConstructionMessage">
                                        <v-icon left small>add_circle</v-icon>
                                        Top Up Wallet
                                    </v-btn>
                                    <v-btn small text class="history-btn" @click="showConstructionMessage">
                                        <v-icon left small>history</v-icon>
                                        History
                                    </v-btn>
                                </div>
                            </div>
                        </v-card-text>
                    </v-card>

                    <!-- Quick Actions -->
                    <v-card class="mb-4 quick-actions-card elevation-2">
                        <v-card-text class="pa-3">
                            <v-layout row wrap class="text-xs-center">
                                <v-flex xs4>
                                    <v-btn icon large class="quick-action-btn" @click="showConstructionMessage">
                                        <v-avatar size="50" color="green lighten-3">
                                            <span class="white--text">5K</span>
                                        </v-avatar>
                                    </v-btn>
                                    <div class="caption mt-1">Quick 5K</div>
                                </v-flex>
                                <v-flex xs4>
                                    <v-btn icon large class="quick-action-btn" @click="showConstructionMessage">
                                        <v-avatar size="50" color="blue lighten-3">
                                            <span class="white--text">10K</span>
                                        </v-avatar>
                                    </v-btn>
                                    <div class="caption mt-1">Quick 10K</div>
                                </v-flex>
                                <v-flex xs4>
                                    <v-btn icon large class="quick-action-btn" @click="showConstructionMessage">
                                        <v-avatar size="50" color="orange lighten-3">
                                            <span class="white--text">20K</span>
                                        </v-avatar>
                                    </v-btn>
                                    <div class="caption mt-1">Quick 20K</div>
                                </v-flex>
                            </v-layout>
                        </v-card-text>
                    </v-card>

                    <!-- Service Selection with construction badge -->
                    <v-card class="mb-4 elevation-2 service-selection-card">
                        <v-card-title class="card-header primary white--text">
                            <v-icon left dark>dashboard</v-icon>
                            Select Service Provider
                            <v-spacer></v-spacer>
                            <v-chip small color="warning" dark class="mr-2">
                                <v-icon small left>engineering</v-icon>
                                DEMO
                            </v-chip>
                            <v-text-field
                                v-model="serviceSearch"
                                append-icon="search"
                                label="Search services..."
                                single-line
                                hide-details
                                dark
                                class="search-field"
                            ></v-text-field>
                        </v-card-title>
                        <v-card-text class="pt-3">
                            <v-alert type="info" class="mb-3" dense>
                                <v-icon small left>info</v-icon>
                                Demonstration service - Payments are not real
                            </v-alert>
                            <v-layout row wrap>
                                <v-flex
                                    xs6 sm4
                                    v-for="service in filteredServices"
                                    :key="service.id"
                                    class="pa-2"
                                >
                                    <v-card
                                        :class="['service-card', selectedService && selectedService.id === service.id ? 'selected' : '']"
                                        @click="selectService(service)"
                                        ripple
                                    >
                                        <v-card-text class="text-xs-center pa-3">
                                            <div class="service-image-container">
                                                <div class="service-image-wrapper">
                                                    <img
                                                        :src="getServiceImage(service.image)"
                                                        :alt="service.name"
                                                        class="service-image"
                                                        @error="handleImageError"
                                                    />
                                                    <div class="service-image-overlay" v-if="service.popular">
                                                        <v-icon small color="white">star</v-icon>
                                                    </div>
                                                </div>
                                            </div>
                                            <div class="service-name font-weight-bold">{{ service.name }}</div>
                                            <div class="service-type caption">{{ service.type }}</div>
                                            <v-chip small v-if="service.popular" color="orange" text-color="white" class="mt-1 popular-chip">
                                                Popular
                                            </v-chip>
                                        </v-card-text>
                                    </v-card>
                                </v-flex>
                            </v-layout>
                        </v-card-text>
                    </v-card>

                    <!-- Payment Form -->
                    <v-card class="elevation-2 payment-form-card" v-if="selectedService">
                        <v-card-title class="card-header primary white--text">
                            <div class="selected-service-header">
                                <div class="selected-service-image">
                                    <img :src="getServiceImage(selectedService.image)" :alt="selectedService.name" class="selected-service-img" />
                                </div>
                                <div class="selected-service-info">
                                    <div>Bill Payment Details</div>
                                    <div class="selected-service-name">{{ selectedService.name }}</div>
                                </div>
                            </div>
                            <v-spacer></v-spacer>
                            <v-chip color="white" text-color="primary" small>
                                {{ selectedService.type }}
                            </v-chip>
                        </v-card-title>
                        <v-card-text class="pt-4">
                            <v-alert type="warning" class="mb-4" dense>
                                <v-icon small left>warning</v-icon>
                                <strong>Simulation only</strong> - No real payment will be processed
                            </v-alert>

                            <v-form ref="paymentForm" v-model="validForm" lazy-validation>
                                <!-- Account Number -->
                                <v-text-field
                                    v-model="accountNumber"
                                    :label="`${selectedService.name} Account Number`"
                                    :placeholder="`Enter your ${selectedService.name} account number`"
                                    :rules="[rules.required, rules.numeric]"
                                    outlined
                                    prepend-inner-icon="account_circle"
                                    class="mb-3"
                                ></v-text-field>

                                <!-- Amount -->
                                <v-text-field
                                    v-model="amount"
                                    label="Amount (FCFA)"
                                    placeholder="Enter amount to pay"
                                    :rules="[rules.required, rules.numeric, rules.minAmount]"
                                    outlined
                                    type="number"
                                    prepend-inner-icon="attach_money"
                                    class="mb-3"
                                >
                                    <template v-slot:append-outer>
                                        <v-chip-group column>
                                            <v-chip small @click="amount = '5000'">5,000</v-chip>
                                            <v-chip small @click="amount = '10000'">10,000</v-chip>
                                            <v-chip small @click="amount = '15000'">15,000</v-chip>
                                            <v-chip small @click="amount = '20000'">20,000</v-chip>
                                        </v-chip-group>
                                    </template>
                                </v-text-field>

                                <!-- Phone Number -->
                                <v-text-field
                                    v-model="phoneNumber"
                                    label="Phone Number for confirmation"
                                    placeholder="e.g., 677123456"
                                    :rules="[rules.required, rules.phone]"
                                    outlined
                                    prepend-inner-icon="phone"
                                    class="mb-4"
                                >
                                    <template v-slot:prepend>
                                        <v-menu offset-y>
                                            <template v-slot:activator="{ on }">
                                                <v-btn small text v-on="on" class="country-code">
                                                    +237
                                                </v-btn>
                                            </template>
                                            <v-list>
                                                <v-list-item @click="countryCode = '+237'">
                                                    <v-list-item-title>Cameroon (+237)</v-list-item-title>
                                                </v-list-item>
                                                <v-list-item @click="countryCode = '+33'">
                                                    <v-list-item-title>France (+33)</v-list-item-title>
                                                </v-list-item>
                                                <v-list-item @click="countryCode = '+1'">
                                                    <v-list-item-title>USA (+1)</v-list-item-title>
                                                </v-list-item>
                                            </v-list>
                                        </v-menu>
                                    </template>
                                </v-text-field>

                                <!-- Payment Summary -->
                                <v-card color="blue lighten-5" flat class="mb-4 payment-summary">
                                    <v-card-text>
                                        <div class="subheading font-weight-bold mb-3 text-xs-center">Payment Summary</div>
                                        <v-layout row class="summary-item">
                                            <v-flex xs6>Service Provider:</v-flex>
                                            <v-flex xs6 class="text-xs-right font-weight-bold">{{ selectedService.name }}</v-flex>
                                        </v-layout>
                                        <v-layout row class="summary-item">
                                            <v-flex xs6>Account Number:</v-flex>
                                            <v-flex xs6 class="text-xs-right">{{ accountNumber || 'N/A' }}</v-flex>
                                        </v-layout>
                                        <v-layout row class="summary-item">
                                            <v-flex xs6>Amount:</v-flex>
                                            <v-flex xs6 class="text-xs-right font-weight-bold primary--text">{{ formatCurrency(amount) || '0 FCFA' }}</v-flex>
                                        </v-layout>
                                        <v-layout row class="summary-item" v-if="selectedService.fee > 0">
                                            <v-flex xs6>Service Fee:</v-flex>
                                            <v-flex xs6 class="text-xs-right">{{ formatCurrency(selectedService.fee) }}</v-flex>
                                        </v-layout>
                                        <v-divider class="my-2"></v-divider>
                                        <v-layout row class="summary-total">
                                            <v-flex xs6 class="subheading">Total Amount:</v-flex>
                                            <v-flex xs6 class="text-xs-right subheading font-weight-bold primary--text">
                                                {{ formatCurrency(totalAmount) }}
                                            </v-flex>
                                        </v-layout>
                                    </v-card-text>
                                </v-card>

                                <!-- Submit Button -->
                                <v-btn
                                    block
                                    large
                                    color="warning"
                                    :disabled="!validForm || !selectedService || processing"
                                    @click="simulatePayment"
                                    :loading="processing"
                                    class="pay-button"
                                    depressed
                                >
                                    <v-icon left>construction</v-icon>
                                    Simulate Payment {{ formatCurrency(totalAmount) }}
                                </v-btn>

                                <div class="text-xs-center mt-2 caption grey--text">
                                    ⚠️ This demo simulates a payment - No real transaction
                                </div>
                            </v-form>
                        </v-card-text>
                    </v-card>

                    <!-- Recent Transactions -->
                    <v-card class="mt-4 elevation-2" v-if="recentTransactions.length > 0">
                        <v-card-title class="card-header primary white--text">
                            <v-icon left dark>history</v-icon>
                            Recent Bill Payments
                            <v-spacer></v-spacer>
                            <v-chip small color="info" dark class="mr-2">
                                TEST DATA
                            </v-chip>
                            <v-btn small icon dark @click="getRecentTransactions">
                                <v-icon>refresh</v-icon>
                            </v-btn>
                        </v-card-title>
                        <v-list two-line class="transaction-list">
                            <template v-for="(transaction, index) in recentTransactions">
                                <v-list-item :key="transaction.id" class="transaction-item">
                                    <v-list-item-avatar class="transaction-avatar">
                                        <img :src="getServiceImageForTransaction(transaction.service)" :alt="transaction.service" />
                                    </v-list-item-avatar>
                                    <v-list-item-content>
                                        <v-list-item-title class="transaction-service">
                                            {{ transaction.service }}
                                        </v-list-item-title>
                                        <v-list-item-subtitle class="transaction-account">
                                            {{ transaction.account }}
                                        </v-list-item-subtitle>
                                        <v-list-item-subtitle class="transaction-date">
                                            {{ formatDate(transaction.date) }}
                                        </v-list-item-subtitle>
                                    </v-list-item-content>
                                    <v-list-item-action>
                                        <v-chip small :color="getStatusColor(transaction.status)" text-color="white" class="transaction-amount">
                                            {{ formatCurrency(transaction.amount) }}
                                        </v-chip>
                                        <div class="transaction-status caption mt-1" :class="transaction.status.toLowerCase()">
                                            {{ transaction.status }}
                                        </div>
                                    </v-list-item-action>
                                </v-list-item>
                                <v-divider :key="'div-' + index" v-if="index < recentTransactions.length - 1"></v-divider>
                            </template>
                        </v-list>
                    </v-card>

                    <!-- Empty State -->
                    <v-card v-else class="mt-4 text-xs-center empty-state" flat>
                        <v-card-text>
                            <v-icon size="64" color="grey lighten-2">receipt</v-icon>
                            <div class="title grey--text text--lighten-1 mt-2">No Recent Payments</div>
                            <div class="caption grey--text">Your bill payment history will appear here</div>
                        </v-card-text>
                    </v-card>
                </v-flex>
            </v-layout>
        </v-container>

        <!-- Top Up Dialog -->
        <v-dialog v-model="showTopUpDialog" max-width="500" persistent>
            <v-card>
                <v-card-title class="card-header warning white--text">
                    <v-icon left>construction</v-icon>
                    Top Up Wallet - DEMO
                </v-card-title>
                <v-card-text class="pt-4">
                    <v-alert type="info" class="mb-4" dense>
                        Feature under development
                    </v-alert>
                    <v-form v-model="validTopUpForm">
                        <v-select
                            v-model="selectedTopUpMethod"
                            :items="topUpMethods"
                            label="Select Top-up Method"
                            outlined
                            prepend-inner-icon="account_balance"
                        ></v-select>

                        <v-text-field
                            v-model="topUpAmount"
                            label="Amount (FCFA)"
                            placeholder="Enter amount"
                            :rules="[rules.required, rules.numeric, rules.minAmount]"
                            outlined
                            type="number"
                            prepend-inner-icon="attach_money"
                        ></v-text-field>

                        <div class="topup-suggestions text-xs-center mb-4">
                            <v-chip v-for="suggestion in [5000, 10000, 20000, 50000]" :key="suggestion"
                                    @click="topUpAmount = suggestion" class="ma-1">
                                {{ formatCurrency(suggestion) }}
                            </v-chip>
                        </div>
                    </v-form>
                </v-card-text>
                <v-card-actions>
                    <v-btn @click="showTopUpDialog = false" text>Cancel</v-btn>
                    <v-spacer></v-spacer>
                    <v-btn color="warning" @click="showConstructionMessage" depressed>
                        <v-icon left>engineering</v-icon>
                        Simulation
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Success Dialog modified for demo -->
        <v-dialog v-model="showSuccessDialog" max-width="450" persistent>
            <v-card class="success-dialog">
                <v-card-text class="text-xs-center pa-6">
                    <div class="success-icon">
                        <v-icon size="80" color="success">check_circle</v-icon>
                    </div>
                    <div class="title mt-4 mb-2 success-title">Payment Simulated Successfully!</div>

                    <v-alert type="info" class="mb-4" dense>
                        This is a simulation - No real payment was processed
                    </v-alert>

                    <p class="success-message">
                        Your payment of <strong>{{ formatCurrency(amount) }}</strong> to
                        <strong>{{ selectedService ? selectedService.name : '' }}</strong>
                        has been successfully simulated.
                    </p>

                    <v-card color="green lighten-5" flat class="mb-4 receipt-card">
                        <v-card-text>
                            <div class="receipt-title">Simulation Receipt</div>
                            <v-layout row class="receipt-item">
                                <v-flex xs6>Reference:</v-flex>
                                <v-flex xs6 class="text-xs-right font-weight-bold">{{ transactionReference }}</v-flex>
                            </v-layout>
                            <v-layout row class="receipt-item">
                                <v-flex xs6>Date:</v-flex>
                                <v-flex xs6 class="text-xs-right">{{ new Date().toLocaleString() }}</v-flex>
                            </v-layout>
                            <v-layout row class="receipt-item">
                                <v-flex xs6>Account:</v-flex>
                                <v-flex xs6 class="text-xs-right">{{ accountNumber }}</v-flex>
                            </v-layout>
                            <v-layout row class="receipt-item">
                                <v-flex xs6>Status:</v-flex>
                                <v-flex xs6 class="text-xs-right font-weight-bold success--text">SIMULATION</v-flex>
                            </v-layout>
                        </v-card-text>
                    </v-card>

                    <div class="success-actions">
                        <v-btn color="primary" @click="resetForm" depressed class="mr-2">
                            New Simulation
                        </v-btn>
                        <v-btn outlined @click="downloadReceipt">
                            <v-icon left>download</v-icon>
                            Download
                        </v-btn>
                    </div>
                </v-card-text>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import axios from 'axios'

export default {
    name: 'BillsPaymentSystem',
    data() {
        return {
            walletBalance: 0,
            selectedService: null,
            accountNumber: '',
            amount: '',
            phoneNumber: '',
            countryCode: '+237',
            validForm: false,
            validTopUpForm: false,
            processing: false,
            showTopUpDialog: false,
            showSuccessDialog: false,
            showTransactionHistory: false,
            showConstructionOverlay: false,
            serviceSearch: '',
            topUpAmount: '',
            selectedTopUpMethod: null,
            transactionReference: this.generateReference(),
            recentTransactions: [],
            services: [
                {
                    id: 1,
                    name: 'ENEO',
                    type: 'Electricity',
                    image: 'eneo.png',
                    color: '#FF9800',
                    popular: true,
                    fee: 100
                },
                {
                    id: 2,
                    name: 'CAMWATER',
                    type: 'Water',
                    image: 'camwater.png',
                    color: '#2196F3',
                    popular: true,
                    fee: 100
                },
                {
                    id: 3,
                    name: 'Canal+',
                    type: 'TV Subscription',
                    image: 'canalplus.png',
                    color: '#F44336',
                    popular: false,
                    fee: 50
                },
                {
                    id: 4,
                    name: 'MTN',
                    type: 'Mobile Credit',
                    image: 'mtn.png',
                    color: '#FFC107',
                    popular: true,
                    fee: 0
                },
                {
                    id: 5,
                    name: 'Orange',
                    type: 'Mobile Credit',
                    image: 'orange.png',
                    color: '#FF5722',
                    popular: true,
                    fee: 0
                },
                {
                    id: 6,
                    name: 'Nexttel',
                    type: 'Mobile Credit',
                    image: 'nexttel.png',
                    color: '#4CAF50',
                    popular: false,
                    fee: 0
                }
            ],
            topUpMethods: [
                'Mobile Money (MTN)',
                'Mobile Money (Orange)',
                'Credit Card',
                'Bank Transfer'
            ],
            rules: {
                required: v => !!v || 'This field is required',
                numeric: v => !v || /^\d+$/.test(v) || 'Must be a valid number',
                minAmount: v => !v || v >= 100 || 'Minimum amount is 100 FCFA',
                phone: v => !v || /^[0-9]{9}$/.test(v) || 'Invalid phone number (e.g., 677123456)'
            }
        }
    },
    computed: {
        filteredServices() {
            if (!this.serviceSearch) return this.services
            return this.services.filter(service =>
                service.name.toLowerCase().includes(this.serviceSearch.toLowerCase()) ||
                service.type.toLowerCase().includes(this.serviceSearch.toLowerCase())
            )
        },
        totalAmount() {
            const baseAmount = parseInt(this.amount) || 0
            const serviceFee = this.selectedService ? this.selectedService.fee : 0
            return baseAmount + serviceFee
        },
        fullPhoneNumber() {
            return this.countryCode + this.phoneNumber
        }
    },
    mounted() {
        document.title = "PettyCash | Bills Payment - DEMO"
        this.getWalletBalance()
        this.getRecentTransactions()
        // Show construction overlay on load
        setTimeout(() => {
            this.showConstructionOverlay = true
        }, 1000)
    },
    methods: {
        selectService(service) {
            this.selectedService = service
            this.accountNumber = ''
            this.amount = ''
        },

        getServiceImage(imageName) {
            try {
                return require(`@/assets/images/services/${imageName}`);
            } catch (e) {
                console.warn(`Image not found: ${imageName}`, e);
                return this.getDefaultServiceImage();
            }
        },

        getServiceImageForTransaction(serviceName) {
            const service = this.services.find(s => s.name === serviceName);
            if (service) {
                return this.getServiceImage(service.image);
            }
            return this.getDefaultServiceImage();
        },

        async getWalletBalance() {
            try {
                const response = await axios.get('/api/v1/wallet/balance/')
                this.walletBalance = response.data.balance
            } catch (error) {
                console.error('Error fetching wallet balance:', error)
                this.walletBalance = 0
            }
        },

        async getRecentTransactions() {
            try {
                const response = await axios.get('/api/v1/wallet/transactions/bills/')
                this.recentTransactions = response.data.slice(0, 5)
            } catch (error) {
                console.error('Error fetching transactions:', error)
                this.recentTransactions = []
            }
        },

        async simulatePayment() {
            if (!this.$refs.paymentForm.validate()) return

            this.processing = true

            // Simulate processing delay
            await new Promise(resolve => setTimeout(resolve, 2000))

            this.showSuccessDialog = true
            this.processing = false

            // Add to transaction history
            this.recentTransactions.unshift({
                id: Date.now(),
                service: this.selectedService.name,
                account: this.accountNumber,
                amount: parseInt(this.amount),
                date: new Date().toISOString(),
                status: 'COMPLETED'
            })
        },

        showConstructionMessage() {
            this.$store.commit('setSnackBarMessage', '🚧 Feature under development')
            this.$store.commit('setSnackBarColor', 'warning')
            this.$store.commit('activateSnackBar', true)
        },

        async processTopUp() {
            this.showTopUpDialog = false
            this.showConstructionMessage()
        },

        quickTopUp(amount) {
            this.topUpAmount = amount
            this.showTopUpDialog = true
        },

        resetForm() {
            this.selectedService = null
            this.accountNumber = ''
            this.amount = ''
            this.phoneNumber = ''
            this.showSuccessDialog = false
            this.transactionReference = this.generateReference()
            if (this.$refs.paymentForm) {
                this.$refs.paymentForm.resetValidation()
            }
        },

        refreshData() {
            this.getWalletBalance()
            this.getRecentTransactions()
            this.showConstructionMessage()
        },

        generateReference() {
            return 'DEMO' + Date.now().toString().slice(-8)
        },

        formatCurrency(amount) {
            if (!amount) return '0 FCFA'
            return new Intl.NumberFormat('fr-FR').format(amount) + ' FCFA'
        },

        formatDate(dateString) {
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            })
        },

        getDefaultServiceImage() {
            return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjYwIiBoZWlnaHQ9IjYwIiByeD0iOCIgZmlsbD0iIzE5NzZEMiIvPgo8cGF0aCBkPSJNMzAgMTVDMzMuMzEzNyAxNSAzNiAxNy42ODYzIDM2IDIxQzM2IDI0LjMxMzcgMzMuMzEzNyAyNyAzMCAyN0MyNi42ODYzIDI3IDI0IDI0LjMxMzcgMjQgMjFDMjQgMTcuNjg2MyAyNi42ODYzIDE1IDMwIDE1WiIgZmlsbD0id2hpdGUiLz4KPHBhdGggZD0iTTE4IDM0VjM3SDQyVjM0QzQyIDMwLjY4NjMgMzkuMzEzNyAyOCAzNiAyOEgyNEMyMC42ODYzIDI4IDE4IDMwLjY4NjMgMTggMzRaIiBmaWxsPSJ3aGl0ZSIvPgo8L3N2Zz4K'
        },

        handleImageError(event) {
            event.target.src = this.getDefaultServiceImage()
        },

        getStatusColor(status) {
            const statusColors = {
                'COMPLETED': 'success',
                'PENDING': 'warning',
                'FAILED': 'error'
            }
            return statusColors[status] || 'grey'
        },

        showError(message) {
            this.$store.commit('setSnackBarMessage', message)
            this.$store.commit('setSnackBarColor', 'red darken-4')
            this.$store.commit('activateSnackBar', true)
        },

        downloadReceipt() {
            this.showConstructionMessage()
        },

        goBack() {
            this.$router.go(-1)
        }
    }
}
</script>

<style scoped>
.bills-payment-container {
    background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
    min-height: 100vh;
    position: relative;
}

.development-banner {
    margin: 0;
    border-radius: 0;
    text-align: center;
    font-weight: 600;
    background: linear-gradient(135deg, #FF9800 0%, #F57C00 100%);
}

.header-animated {
    background: linear-gradient(135deg, #1976D2 0%, #1565C0 100%) !important;
    box-shadow: 0 4px 20px rgba(25, 118, 210, 0.3);
}

.toolbar-title {
    font-weight: 600;
    letter-spacing: 0.5px;
    display: flex;
    align-items: center;
}

.construction-chip {
    font-size: 0.7rem;
    height: 20px;
}

.back-btn, .refresh-btn {
    transition: transform 0.3s ease;
}

.back-btn:hover {
    transform: translateX(-2px);
}

.refresh-btn:hover {
    transform: rotate(180deg);
}

/* Construction overlay */
.construction-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}

.construction-content {
    background: white;
    padding: 2rem;
    border-radius: 12px;
    text-align: center;
    max-width: 400px;
    margin: 1rem;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.construction-title {
    color: #FF9800;
    margin: 1rem 0;
    font-weight: 600;
}

.construction-message {
    color: #666;
    margin-bottom: 1.5rem;
    line-height: 1.5;
}

/* Construction badge on cards */
.construction-badge {
    position: absolute;
    top: -8px;
    right: -8px;
    background: #FF9800;
    color: white;
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 0.7rem;
    font-weight: bold;
    z-index: 10;
    box-shadow: 0 2px 8px rgba(255, 152, 0, 0.5);
}

/* Wallet Card */
.wallet-card {
    border-radius: 16px;
    box-shadow: 0 8px 32px rgba(25, 118, 210, 0.3) !important;
    position: relative;
    overflow: hidden;
}

.wallet-card::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.1), transparent);
    transform: rotate(45deg);
    animation: shine 3s infinite;
}

.wallet-content {
    position: relative;
    z-index: 2;
}

.wallet-icon {
    font-size: 3rem;
    margin-bottom: 8px;
    opacity: 0.9;
}

.wallet-label {
    opacity: 0.8;
    letter-spacing: 1px;
}

.wallet-amount {
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
    margin: 8px 0;
}

.wallet-actions {
    margin-top: 16px;
}

.topup-btn, .history-btn {
    margin: 0 4px;
    transition: all 0.3s ease;
}

.topup-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(255, 255, 255, 0.3);
}

/* Quick Actions */
.quick-actions-card {
    border-radius: 12px;
}

.quick-action-btn {
    transition: transform 0.3s ease;
}

.quick-action-btn:hover {
    transform: scale(1.1);
}

/* Service Cards */
.service-selection-card {
    border-radius: 12px;
    position: relative;
}

.card-header {
    border-radius: 12px 12px 0 0 !important;
}

.search-field {
    max-width: 200px;
}

.service-card {
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    border: 2px solid transparent;
    position: relative;
    overflow: hidden;
}

.service-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: var(--service-color, #1976D2);
    transform: scaleX(0);
    transition: transform 0.3s ease;
}

.service-card:hover::before {
    transform: scaleX(1);
}

.service-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.service-card.selected {
    border-color: #1976D2;
    box-shadow: 0 8px 25px rgba(25, 118, 210, 0.3);
    transform: translateY(-2px);
}

.service-card.selected::before {
    transform: scaleX(1);
}

/* Service Image */
.service-image-container {
    margin-bottom: 12px;
}

.service-image-wrapper {
    position: relative;
    display: inline-block;
}

.service-image {
    width: 60px;
    height: 60px;
    border-radius: 12px;
    object-fit: contain;
    background: white;
    padding: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;
}

.service-card:hover .service-image {
    transform: scale(1.1);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
}

.service-image-overlay {
    position: absolute;
    top: -4px;
    right: -4px;
    background: #FF9800;
    border-radius: 50%;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.service-name {
    color: #333;
    margin-bottom: 4px;
    font-size: 0.9rem;
}

.service-type {
    color: #666;
    font-size: 0.8rem;
}

.popular-chip {
    font-size: 0.7rem;
}

/* Selected Service Header */
.selected-service-header {
    display: flex;
    align-items: center;
    gap: 12px;
}

.selected-service-img {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    object-fit: contain;
    background: white;
    padding: 4px;
}

.selected-service-info {
    line-height: 1.2;
}

.selected-service-name {
    font-size: 0.9rem;
    opacity: 0.9;
}

/* Payment Form */
.payment-form-card {
    border-radius: 12px;
}

.payment-summary {
    border-radius: 8px;
    border-left: 4px solid #1976D2;
}

.summary-item, .summary-total {
    padding: 4px 0;
}

.pay-button {
    border-radius: 8px;
    height: 48px !important;
    font-weight: 600;
    letter-spacing: 0.5px;
    text-transform: none;
    transition: all 0.3s ease;
}

.pay-button:not(.v-btn--disabled):hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(255, 152, 0, 0.4);
}

/* Transaction List */
.transaction-list {
    border-radius: 0 0 12px 12px;
}

.transaction-item {
    transition: background-color 0.3s ease;
}

.transaction-item:hover {
    background-color: #f5f5f5;
}

.transaction-avatar {
    transition: transform 0.3s ease;
}

.transaction-item:hover .transaction-avatar {
    transform: scale(1.1);
}

.transaction-avatar img {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    object-fit: contain;
    background: white;
    padding: 4px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.transaction-service {
    font-weight: 600;
    color: #333;
}

.transaction-account {
    color: #666;
}

.transaction-date {
    color: #999;
    font-size: 0.75rem;
}

.transaction-amount {
    font-weight: 600;
}

.transaction-status {
    font-weight: 600;
    text-transform: uppercase;
    font-size: 0.7rem;
}

.transaction-status.completed {
    color: #4CAF50;
}

.transaction-status.pending {
    color: #FF9800;
}

.transaction-status.failed {
    color: #F44336;
}

/* Success Dialog */
.success-dialog {
    border-radius: 16px;
}

.success-icon {
    animation: bounceIn 0.6s ease-out;
}

.success-title {
    color: #4CAF50;
    font-weight: 600;
}

.success-message {
    color: #666;
    line-height: 1.6;
}

.receipt-card {
    border-radius: 8px;
    border: 1px solid #4CAF50;
}

.receipt-title {
    font-weight: 600;
    color: #4CAF50;
    text-align: center;
    margin-bottom: 12px;
}

.receipt-item {
    padding: 2px 0;
    font-size: 0.9rem;
}

.success-actions {
    margin-top: 20px;
}

/* Empty State */
.empty-state {
    border-radius: 12px;
    padding: 40px 20px;
}

/* Top-up Suggestions */
.topup-suggestions .v-chip {
    cursor: pointer;
    transition: all 0.3s ease;
}

.topup-suggestions .v-chip:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Country Code */
.country-code {
    min-width: 80px;
    border-right: 1px solid #e0e0e0;
    margin-right: 8px;
}

/* Animations */
@keyframes shine {
    0% {
        transform: rotate(45deg) translateX(-100%);
    }
    100% {
        transform: rotate(45deg) translateX(100%);
    }
}

@keyframes bounceIn {
    0% {
        opacity: 0;
        transform: scale(0.3);
    }
    50% {
        opacity: 1;
        transform: scale(1.05);
    }
    70% {
        transform: scale(0.9);
    }
    100% {
        opacity: 1;
        transform: scale(1);
    }
}

/* Responsive Design */
@media (max-width: 959px) {
    .wallet-amount {
        font-size: 2rem !important;
    }
}

@media (max-width: 599px) {
    .wallet-actions {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    .topup-btn, .history-btn {
        margin: 0;
    }

    .service-name {
        font-size: 0.8rem;
    }

    .service-image {
        width: 50px;
        height: 50px;
    }

    .selected-service-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
    }

    .success-actions {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    .construction-content {
        padding: 1.5rem;
        margin: 0.5rem;
    }
}

/* Image backgrounds */
.service-image,
.selected-service-img,
.transaction-avatar img {
    background: white;
    border: 1px solid #e0e0e0;
}

/* Styles for construction badges */
.v-chip--warning {
    background: linear-gradient(135deg, #FF9800 0%, #F57C00 100%) !important;
}
</style>